import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, Cross2Icon as CloseIcon } from '@radix-ui/react-icons'
import * as RadixLabel from '@radix-ui/react-label'
import { DatePicker, Spin } from 'antd'
import useMessage from 'antd/es/message/useMessage'
import { AxiosError } from 'axios'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import { InspectionThumbnail } from 'features/assetDatabase/components/InspectionThumbnail'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { sanitizeFilenameForRolodex } from 'features/files/utils/fileUploadUtils'
import { StyledSelectDiv } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/sharedStyles'
import { StyledFormInput } from 'features/rightDrawer/drawerStates/crudWorkflows/utils/utils'
import { RightSideDrawerProps } from 'features/rightDrawer/utils/interfaces'
import { useRolodexEntries } from 'features/rolodex'
import * as mime from 'mime-types'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form'
import { CustomProvider, SelectPicker } from 'rsuite'
import styled from 'styled-components'
import {
  DropdownOption,
  GeckoAllowableFileTypes,
  GeckoFileCategory,
  GeckoFileCategoryLabels,
  GeckoOrganization,
  GeckoReportJSON,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import { checkFiles, FileSchema } from './editFileSchema'

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Space = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  height: 48px;
  cursor: pointer;
  border-radius: 4px;
  margin-top: 16px;

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const StyledInput = styled.input`
  display: none;
`

const StyledFileLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border: none;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const FileInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 4px;
  padding: 8px;
`

const FileName = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`
const StyledComponentSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StyledCheckboxContainer = styled.div`
  padding: 8px;
  display: flex;
  height: 48px;
  flex-direction: row;
  align-items: center;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 4px;
`

const StyledCheckboxIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 4px;
  margin-right: 14px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  justify-content: center;
  align-items: center;
  display: flex;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const StyledUploadContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 30px;
  border-radius: 4px;
  margin-left: 8px;
  margin-right: 8px;
  padding: 8px;
  background-color: ${({ theme }) => theme.forecast.cardBackground};
`

export const UploadFileProps: RightSideDrawerProps = {
  title: 'Upload File',
}

export type UploadFileDependencies = {
  setReportUploadStatusById: Dispatch<
    SetStateAction<Record<string, ReportUploadStatus>>
  >
  forceRefresh: () => void
}

export enum ReportUploadStatus {
  Uploading = 'Uploading...',
  Success = 'Success',
  Error = 'Error',
}

export type UploadFileProps = {
  organization: GeckoOrganization | undefined
  onClose: () => void
  disableComponentSelection?: boolean
  siteID: string | undefined
  unitID: string | undefined
}

export const UploadFile: React.FC<UploadFileProps> = ({
  organization,
  onClose,
  disableComponentSelection = false,
  siteID,
  unitID,
}) => {
  const { rolodexPatch, rolodexUploadFile, rolodexUploadImage, rolodexDelete } =
    useRolodexEdit()
  const { components, inspections } = useRolodexEntries({
    type: [RolodexEntryType.COMPONENT, RolodexEntryType.INSPECTION],
    tags: { site: siteID ?? '', unit: unitID ?? '' },
  })
  const [fileCategory, setfileCategory] = useState<GeckoFileCategory>(
    GeckoFileCategory.OTHER
  )
  const [documentDate, setDocumentDate] = useState<dayjs.Dayjs | null>(null)

  const [reportUploadStatusById, setReportUploadStatusById] = React.useState<
    Record<string, ReportUploadStatus>
  >({})

  const [isUploading, setIsUploading] = React.useState<boolean>(false)

  const totalUploads = Object.keys(reportUploadStatusById).length
  const successfulUploads = Object.values(reportUploadStatusById).filter(
    (status) => status === ReportUploadStatus.Success
  ).length

  useEffect(() => {
    if (successfulUploads === totalUploads && totalUploads > 0) {
      const timer = setTimeout(() => {
        setReportUploadStatusById({})
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [successfulUploads, totalUploads, setReportUploadStatusById])

  const [messageApi] = useMessage()

  const form = useForm<FileSchema>({
    resolver: zodResolver(FileSchema),
    defaultValues: {
      fileCategory: 'other',
    },
  })
  const { handleSubmit, control } = form
  const { isValid } = form.formState

  const watchedInspections = useWatch({
    control,
    name: 'inspections',
  })

  const { inspections: inspectionsForSubmission } = useRolodexEntries({
    type: RolodexEntryType.INSPECTION,
    ids: watchedInspections ?? [],
  })

  const watchedComponents = useWatch({
    control,
    name: 'components',
  })

  const { components: componentsForSubmission } = useRolodexEntries({
    type: RolodexEntryType.COMPONENT,
    ids: watchedComponents ?? [],
  })

  const watchedFiles = useWatch({
    control,
    name: 'files',
  })

  const disableFileCategory = (category: GeckoFileCategory) => {
    if (!watchedFiles || watchedFiles.length === 0) {
      return false
    }
    const {
      hasNoImages,
      hasOnlyDocOrDocxOrPDF,
      hasOnlyImages,
      hasOnlyOnePDF,
      hasOnlyPDFs,
    } = checkFiles(watchedFiles)

    if (
      category === GeckoFileCategory.CONSTRUCTION_DRAWING ||
      category === GeckoFileCategory.INSPECTION_WALKDOWN_REPORT
    ) {
      return !hasOnlyPDFs
    }
    if (category === GeckoFileCategory.NON_GECKO_REPORT) {
      return !hasOnlyDocOrDocxOrPDF
    }

    if (category === GeckoFileCategory.GECKO_REPORT) {
      return !hasOnlyOnePDF
    }

    if (category === GeckoFileCategory.IMAGE) {
      return !hasOnlyImages
    }

    if (category === GeckoFileCategory.OTHER) {
      return !hasNoImages
    }

    return true
  }

  const getFileTypeErrorMessage = (category: GeckoFileCategory) => {
    switch (category) {
      case GeckoFileCategory.CONSTRUCTION_DRAWING:
        return 'Construction drawings must be pdfs.'
      case GeckoFileCategory.IMAGE:
        return 'Images must be image file types.'
      case GeckoFileCategory.OTHER:
        return 'Other files must not be images.'
      case GeckoFileCategory.GECKO_REPORT:
        return 'Gecko reports must be pdfs and can only be uploaded one at a time.'
      case GeckoFileCategory.NON_GECKO_REPORT:
        return 'Non-gecko reports must be pdfs, doc, or docx.'
      case GeckoFileCategory.INSPECTION_WALKDOWN_REPORT:
        return 'Inspection walkdown reports must be pdfs.'
      default:
        return 'Selected file does not match type'
    }
  }

  const fileCategoryOptions: Array<DropdownOption> = Object.values(
    GeckoFileCategory
  )
    .filter((value) => value !== GeckoFileCategory.IMAGE)
    .map((value) => ({
      value,
      label: GeckoFileCategoryLabels[value],
    }))

  const handleInspectionToggle = (
    inspectionId: string,
    field: ControllerRenderProps<FileSchema, 'inspections'>
  ) => {
    const checked = field.value?.includes(inspectionId) ?? false
    if (checked) {
      field.onChange(
        (field.value ?? []).filter((id: string) => id !== inspectionId)
      )
    } else {
      field.onChange([...(field.value ?? []), inspectionId])
    }
  }

  const onSubmit: SubmitHandler<FileSchema> = async (data) => {
    setIsUploading(true)
    const uploadFile = async (file: File) => {
      if (!file) {
        messageApi.error('Please select a valid file to upload!')
        return
      }

      const uuid = uuidv4()

      // Get the component ID for every explicitly selected component. Additionally, add the component ID for
      // all inspections selected (regardless if the component was already explicitly selected).
      // Ensure the list is de-duped.
      const componentIDs = [
        ...new Set([
          ...componentsForSubmission.map((component) => component.id),
          ...inspectionsForSubmission.map(
            (inspection) => inspection.tags.component
          ),
        ]),
      ]

      const jobIDs = [
        ...new Set(
          inspectionsForSubmission.map((inspection) => inspection.tags.job)
        ),
      ]

      const inspectionIDs = [
        ...new Set(inspectionsForSubmission.map((inspection) => inspection.id)),
      ]

      const patchBodyData: GeckoReportJSON = {
        visible_name: file.name,
        file_type: mime.extension(file.type),
        upload_date: format(Date.now(), 'yyyy-MM-dd HH:mm:ss.SSSSS'),
        document_date: documentDate
          ? documentDate.format('YYYY-MM-DD HH:mm:ss.00000')
          : undefined,
        size: file.size,
        units: [unitID ?? ''],
        components: componentIDs,
        jobs: jobIDs,
        inspections: inspectionIDs,
        file_category: data.fileCategory as GeckoFileCategory,
        file_source: data.fileSource,
      }

      const patchBody = {
        id: uuid,
        type: RolodexEntryType.REPORT,
        data: patchBodyData as unknown as Record<string, unknown>,
        tags: {
          unit: unitID ?? '',
          site: siteID ?? '',
          organization: organization?.id ?? '',
        },
      }

      let patchResult
      let uploadResult
      // Must patch rolodex before uploading file
      try {
        patchResult = await rolodexPatch([patchBody as RolodexEntryPatchJson])
      } catch (e) {
        messageApi.error(`Error on upload ${e}`)
      }
      if (patchResult && patchResult.status === 200) {
        try {
          setReportUploadStatusById((prev) => ({
            ...prev,
            [uuid]: ReportUploadStatus.Uploading,
          }))
          const uploadPath = sanitizeFilenameForRolodex(file.name)
          uploadResult =
            fileCategory === GeckoFileCategory.IMAGE
              ? await rolodexUploadImage(uuid, file, uploadPath)
              : await rolodexUploadFile(uuid, file, uploadPath)
        } catch (e) {
          if (!!e && e instanceof AxiosError && !!e.message) {
            messageApi.error(`Error on upload ${e}`)
            setReportUploadStatusById((prev) => ({
              ...prev,
              [uuid]: ReportUploadStatus.Error,
            }))
          }
        }
        // If upload fails, delete the patch entry
        if (!uploadResult || uploadResult.status !== 200) {
          await rolodexDelete(patchBody.id)
        } else {
          setReportUploadStatusById((prev) => ({
            ...prev,
            [uuid]: ReportUploadStatus.Success,
          }))
        }
      }
    }

    await Promise.all(data.files.map((file: File) => uploadFile(file)))
    setIsUploading(false)
    setDocumentDate(null)
    onClose()
  }

  return (
    <FormProvider {...form}>
      <Space>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <FormItem>
            <Controller
              name='fileCategory'
              control={control}
              render={({ field: { onBlur, onChange, ref, value } }) => (
                <StyledSelectDiv>
                  <label>File Category</label>
                  <CustomProvider theme='dark'>
                    <SelectPicker
                      onChange={(value) => {
                        onChange(value)
                        setfileCategory(value as GeckoFileCategory)
                      }}
                      ref={ref}
                      onBlur={onBlur}
                      searchable={true}
                      value={value}
                      data={fileCategoryOptions}
                      data-testid='file-category-select'
                    />
                  </CustomProvider>
                  {disableFileCategory(value as GeckoFileCategory) && (
                    <span role='alert'>
                      {getFileTypeErrorMessage(value as GeckoFileCategory)}
                    </span>
                  )}
                </StyledSelectDiv>
              )}
            />
          </FormItem>
          <FormItem>
            <RadixLabel.Root>File(s)</RadixLabel.Root>
            <Controller
              name='files'
              control={control}
              render={({ field }) => (
                <>
                  <StyledInput
                    type='file'
                    id='file-upload'
                    multiple={fileCategory !== GeckoFileCategory.GECKO_REPORT}
                    accept={GeckoAllowableFileTypes[fileCategory]}
                    onChange={(e) => {
                      if (e.target.files) {
                        field.onChange(Array.from(e.target.files))
                      }
                    }}
                  />
                  {(!field.value || field.value.length === 0) && (
                    <StyledFileLabel htmlFor='file-upload'>
                      Choose File
                    </StyledFileLabel>
                  )}
                  {field.value && field.value.length > 0 && (
                    <FileInfo>
                      {field.value.map((file: File, index: number) => (
                        <FileName
                          key={index}
                          onClick={() => {
                            const newFiles = field.value?.filter(
                              (_: File, i: number) => i !== index
                            )
                            field.onChange(
                              newFiles && newFiles.length > 0
                                ? newFiles
                                : undefined
                            )
                          }}>
                          {file.name}
                          <CloseIcon />
                        </FileName>
                      ))}
                    </FileInfo>
                  )}
                </>
              )}
            />
          </FormItem>
          <FormItem>
            <RadixLabel.Root>File Source (optional)</RadixLabel.Root>
            <Controller
              name='fileSource'
              control={control}
              render={({ field }) => (
                <StyledFormInput
                  {...field}
                  placeholder='Enter the source of the file (Drive, SAP, etc.)'
                />
              )}
            />
          </FormItem>
          <FormItem>
            <RadixLabel.Root>Document Date (optional)</RadixLabel.Root>
            <DatePicker
              style={{ width: '100%' }}
              value={documentDate}
              onChange={(date) => setDocumentDate(date ?? null)}
              placeholder='Select document date'
            />
          </FormItem>
          {fileCategory !== GeckoFileCategory.GECKO_REPORT &&
            !disableComponentSelection && (
              <FormItem>
                <RadixLabel.Root>Component Tag(s)</RadixLabel.Root>
                <Controller
                  name='components'
                  control={control}
                  render={({ field }) => (
                    <StyledComponentSelectContainer>
                      {components.map((component) => (
                        <StyledCheckboxContainer key={component.id}>
                          <StyledCheckboxIcon
                            role='checkbox'
                            data-testid={`component-checkbox-${component.data.name}`}
                            onClick={() => {
                              const checked =
                                field.value?.includes(component.id) ?? false
                              if (checked) {
                                form.setValue(
                                  'components',
                                  (field.value ?? []).filter(
                                    (id: string) => id !== component.id
                                  ),
                                  {
                                    shouldValidate: true,
                                  }
                                )
                              } else {
                                form.setValue(
                                  'components',
                                  [...(field.value ?? []), component.id],
                                  {
                                    shouldValidate: true,
                                  }
                                )
                              }
                            }}>
                            {field.value?.includes(component.id) && (
                              <CheckIcon />
                            )}
                          </StyledCheckboxIcon>
                          {component.data.name}
                        </StyledCheckboxContainer>
                      ))}
                    </StyledComponentSelectContainer>
                  )}
                />
              </FormItem>
            )}
          {(fileCategory === GeckoFileCategory.GECKO_REPORT ||
            fileCategory === GeckoFileCategory.IMAGE) && (
            <FormItem>
              <RadixLabel.Root>Inspections</RadixLabel.Root>
              <Controller
                name='inspections'
                control={control}
                render={({ field }) => (
                  <div data-testid='inspection-checkbox'>
                    {[...inspections]
                      .sort(
                        (a, b) =>
                          new Date(b.data.date).getTime() -
                          new Date(a.data.date).getTime()
                      )
                      .map((inspection) => (
                        <StyledCheckboxContainer key={inspection.id}>
                          <StyledCheckboxIcon
                            role='checkbox'
                            data-testid={`inspection-checkbox-${inspection.id}`}
                            onClick={() =>
                              handleInspectionToggle(inspection.id, field)
                            }>
                            {field.value?.includes(inspection.id) && (
                              <CheckIcon />
                            )}
                          </StyledCheckboxIcon>
                          <InspectionThumbnail
                            inspectionID={inspection.id}
                            unitID={unitID ?? ''}
                            linkable={false}
                          />
                        </StyledCheckboxContainer>
                      ))}
                  </div>
                )}
              />
            </FormItem>
          )}
          <StyledButton
            disabled={!isValid || isUploading}
            type={'submit'}
            data-testid='upload-file-button'>
            {!isUploading ? 'Upload File(s)' : <Spin spinning={true} />}
          </StyledButton>
        </StyledForm>
        {totalUploads > 0 && (
          <StyledUploadContainer>
            Files uploading... {successfulUploads} of {totalUploads}
          </StyledUploadContainer>
        )}
      </Space>
    </FormProvider>
  )
}
